<template>
  <div class="group-field">
    <!-- Language fields layout with improved UI -->
    <div class="language-fields">
      <!-- Language tabs -->
      <div class="language-tabs">
        <v-chip-group
          v-model="activeTab"
          mandatory
        >
          <v-chip
            v-for="(item, index) in internalValue"
            :key="index"
            small
            class="mr-1"
            :class="{'primary': activeTab === index}"
          >
            {{ $t(item[labelKey] ? item[labelKey].toLowerCase() : '') }}
            <v-icon
              v-if="!getCompletionStatus(item)"
              right
              small
              class="ml-1"
            >
              mdi-alert-circle
            </v-icon>
          </v-chip>
        </v-chip-group>

        <!-- Global actions -->
        <div class="language-actions">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                x-small
                color="primary"
                v-bind="attrs"
                v-on="on"
                @click="copyToAll"
                :disabled="internalValue.length < 2"
              >
                <v-icon small>
                  mdi-content-copy
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('Copy values between languages') }}</span>
          </v-tooltip>
        </div>
      </div>

      <!-- Active language section -->
      <div
        v-if="internalValue.length > 0 && activeTab < internalValue.length"
      >
        <!-- Language fields - Improved layout -->
        <div class="language-fields-container">
          <div
            v-for="childFieldKey in childKeys"
            :key="childFieldKey"
            class="language-field-item"
          >
            <div class="">
              <!-- Field input on the right -->
              <div
                class="field-input"
                style="width: 100%"
              >
                <v-text-field
                  v-model="
                    internalValue[activeTab][childFieldKey]"
                  :placeholder="$t('PleaseInputTheX', { configItem: $t(getFieldLabel(childFieldKey)) })"
                  dense
                  outlined
                  :error-messages="getFieldValidationError(internalValue[activeTab], childFieldKey)"
                  @input="onFieldInput(activeTab, childFieldKey)"
                  @change="syncValues"
                  hide-details="auto"
                >
                  <template #append>
                    <div
                      style="height: 24px"
                      class="text-caption d-flex align-center"
                    >
                      {{ $t(getFieldLabel(childFieldKey)) }}
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-if="isFieldRequired(childFieldKey)"
                            x-small
                            color="error"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-asterisk
                          </v-icon>
                        </template>
                        <span>{{ $t('Required field') }}</span>
                      </v-tooltip>
                    </div>
                  </template>
                </v-text-field>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else
        class="empty-state"
      >
        <v-alert
          type="info"
          outlined
          dense
        >
          {{ $t('No language data available') }}
        </v-alert>
      </div>
    </div>
  </div>
</template>

<script>
import IKUtils from 'innerken-js-utils'

/**
 * GroupField Component
 *
 * A component for managing multilingual content with support for multiple languages.
 * Features:
 * - Tab-based navigation between languages
 * - Visual indicators for completion status
 * - Copy functionality between languages
 * - Support for adding missing languages
 * - Improved validation and error handling
 */
export default {
  name: 'GroupField',
  props: {
    /**
     * Array of language objects to edit
     * Each object should have a language identifier and content fields
     * @example [{ lang: 'EN', title: 'Hello', description: 'World' }]
     */
    value: {
      type: Array,
      default: () => []
    },
    /**
     * Title of the group field (for backward compatibility)
     */
    groupName: {
      type: String,
      default: ''
    },
    /**
     * Array of field keys to edit for each language
     * @example ['title', 'description', 'keywords']
     */
    childKeys: {
      type: Array,
      default: () => []
    },
    /**
     * Key in each language object that contains the language identifier
     * @default 'lang'
     */
    labelKey: {
      type: String,
      default: 'lang'
    },
    /**
     * Key for child labels (maintained for backward compatibility)
     */
    childLabelKey: {
      type: String,
      default: 'lang'
    },
    /**
     * Array of supported languages (ISO codes)
     * @default ['ZH', 'EN', 'DE']
     */
    supportedLanguages: {
      type: Array,
      default: () => ['ZH', 'EN', 'DE']
    },
    /**
     * Primary language code (required fields)
     * @default 'ZH'
     */
    primaryLanguage: {
      type: String,
      default: 'ZH'
    },
    /**
     * Array of required field keys
     * If empty, all fields are considered required
     */
    requiredFields: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      // Active tab index for language selection
      activeTab: 0,
      // Internal value to track changes
      internalValue: [],
      // Field validation errors
      fieldErrors: {}
    }
  },
  computed: {
    // Get supported languages from props or use default
    normalizedSupportedLanguages () {
      // Use the prop directly to avoid infinite recursion
      return this.$props.supportedLanguages.map(lang => lang.toUpperCase())
    },
    // Language completion status
    completionStatus () {
      const total = this.internalValue.length
      const completed = this.internalValue.filter(item => this.getCompletionStatus(item)).length
      return `${completed}/${total}`
    },
    // Check if there are missing languages
    hasMissingLanguages () {
      const currentLanguages = this.internalValue.map(item =>
        item[this.labelKey]?.toUpperCase()
      )

      return this.normalizedSupportedLanguages.some(lang =>
        !currentLanguages.includes(lang)
      )
    }
  },
  watch: {
    // Watch for changes to the value prop
    value: {
      handler (newVal) {
        if (newVal && Array.isArray(newVal)) {
          // Filter to only include supported languages
          const filteredValue = newVal.filter(item =>
            this.normalizedSupportedLanguages.includes(item[this.labelKey]?.toUpperCase())
          )

          // If we have values after filtering, use them
          if (filteredValue.length > 0) {
            this.internalValue = IKUtils.deepCopy(filteredValue)
          } else {
            // If no supported languages in the input, initialize with empty array
            this.internalValue = []
          }
        } else {
          this.internalValue = []
        }
      },
      immediate: true
    }
  },
  mounted () {
    // If no languages are provided, add the supported languages
    if (this.internalValue.length === 0) {
      // Add all supported languages
      this.normalizedSupportedLanguages.forEach(lang => {
        // Skip if already exists
        if (this.internalValue.some(item => item[this.labelKey]?.toUpperCase() === lang)) {
          return
        }

        const newLangEntity = {
          [this.labelKey]: lang
        }

        // Initialize with empty values for all child keys
        this.childKeys.forEach(key => {
          newLangEntity[key] = ''
        })

        this.internalValue.push(newLangEntity)
      })

      // Sort languages with primary language first
      this.sortLanguages()

      this.syncValues()
    }
  },
  methods: {
    /**
     * Check if a language is the primary language
     */
    isPrimaryLanguage (langCode) {
      if (!langCode) return false
      return langCode.toUpperCase() === this.primaryLanguage.toUpperCase()
    },
    /**
     * Check if a field is required
     */
    isFieldRequired (fieldKey) {
      // If requiredFields is empty, all fields are required
      return fieldKey === 'name'
    },
    /**
     * Convert a camelCase key to Title Case for display
     */
    getFieldLabel (key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
    },
    /**
     * Get a consistent color for a language code
     */
    getLanguageColor (langCode) {
      if (!langCode) return 'grey'

      // Default colors for common languages
      const langColors = {
        ZH: 'red',
        EN: 'blue',
        DE: 'amber darken-2',
        FR: 'indigo',
        ES: 'orange',
        JA: 'purple',
        KO: 'teal'
      }

      return langColors[langCode.toUpperCase()] || 'grey'
    },
    /**
     * Check if a language entity has all required fields filled
     */
    getCompletionStatus (item) {
      if (!item) return false

      // If requiredFields is specified, only check those fields
      const fieldsToCheck = this.requiredFields.length > 0
        ? this.requiredFields
        : this.childKeys

      return fieldsToCheck.every(key =>
        item[key] !== undefined &&
          item[key] !== null &&
          item[key] !== ''
      )
    },
    /**
     * Get validation error for a field
     */
    getFieldValidationError (item, fieldKey) {
      if (!item) return ''
      const key = `${item[this.labelKey]}_${fieldKey}`
      return this.fieldErrors[key] || ''
    },
    /**
     * Handle field input
     */
    onFieldInput (index, fieldKey) {
      const item = this.internalValue[index]
      if (!item) return

      const key = `${item[this.labelKey]}_${fieldKey}`

      // Clear error when user types
      if (this.fieldErrors[key]) {
        this.fieldErrors[key] = ''
      }

      // Validate required fields
      if (this.isFieldRequired(fieldKey) &&
          (item[fieldKey] === undefined || item[fieldKey] === null || item[fieldKey] === '')) {
        this.fieldErrors[key] = this.isPrimaryLanguage(item[this.labelKey])
          ? 'This field is required'
          : 'Recommended to fill this field'
      }
    },
    /**
     * Check if a field can be copied to other languages
     */
    canCopyField (index, fieldKey) {
      if (index >= this.internalValue.length) return false
      const value = this.internalValue[index][fieldKey]
      return value !== undefined && value !== null && value !== ''
    },
    /**
     * Copy a specific field to other languages
     */
    copyFieldToOtherLanguages (sourceIndex, fieldKey) {
      if (sourceIndex >= this.internalValue.length) return
      const sourceValue = this.internalValue[sourceIndex][fieldKey]

      if (!sourceValue) return

      // Copy to all other languages
      this.internalValue.forEach((item, index) => {
        if (index !== sourceIndex) {
          item[fieldKey] = sourceValue
        }
      })

      // Emit the updated value
      this.syncValues()
    },
    /**
     * Emit input event when values change
     * Also validates all fields
     */
    syncValues () {
      // Validate all fields before emitting
      this.validateAllFields()

      // Emit the updated value
      this.$emit('input', this.internalValue)
    },
    /**
     * Find a language entity in the array by language code
     */
    findLangEntity (lang) {
      return this.internalValue.find(
        d => d[this.labelKey] && d[this.labelKey].toUpperCase() === lang.toUpperCase()
      ) || null
    },
    /**
     * Sort languages with primary language first, then alphabetically
     */
    sortLanguages () {
      this.internalValue.sort((a, b) => {
        const langA = a[this.labelKey]?.toUpperCase()
        const langB = b[this.labelKey]?.toUpperCase()

        // Primary language always comes first
        if (langA === this.primaryLanguage.toUpperCase()) return -1
        if (langB === this.primaryLanguage.toUpperCase()) return 1

        // Then sort alphabetically
        return langA?.localeCompare(langB || '')
      })
    },
    /**
     * Copy values from one language to all others
     * Finds the first non-empty value for each field and copies it to empty fields
     */
    copyToAll () {
      // Get language entities that exist in our data
      const languages = this.internalValue

      if (languages.length < 2) {
        return // Need at least 2 languages to copy between
      }

      // For each child key
      for (const fieldKey of this.childKeys) {
        // Find the first non-empty value
        const values = languages.map(lang => lang[fieldKey])
        const target = values.find(val => val !== undefined && val !== null && val !== '')

        if (!target) continue // Skip if no value found

        // Copy to empty fields
        for (const lang of languages) {
          if (!lang[fieldKey]) {
            lang[fieldKey] = target
          }
        }
      }

      // Emit the updated value
      this.syncValues()
    },
    /**
     * Validate all fields in all languages
     */
    validateAllFields () {
      // Clear all errors first
      this.fieldErrors = {}

      // Validate each language
      this.internalValue.forEach((item) => {
        // Get the language code
        const langCode = item[this.labelKey]
        const isPrimary = this.isPrimaryLanguage(langCode)

        // Validate each field
        this.childKeys.forEach(fieldKey => {
          if (this.isFieldRequired(fieldKey) &&
              (item[fieldKey] === undefined || item[fieldKey] === null || item[fieldKey] === '')) {
            const key = `${langCode}_${fieldKey}`
            this.fieldErrors[key] = isPrimary
              ? 'This field is required'
              : 'Recommended to fill this field'
          }
        })
      })
    }
  }
}
</script>

<style scoped>
.group-field {
  width: 100%;
  margin-bottom: 16px;
}

.language-fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.language-tabs {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.language-actions {
  display: flex;
  gap: 8px;
}

.language-section {
  border-radius: 4px;
  transition: all 0.2s ease;
}

.language-fields-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.language-field-item {
  width: 100%;
  margin-bottom: 4px;
}

.field-label {
  width: 30%;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.field-input {
  width: 70%;
}

.empty-state {
  padding: 16px;
  text-align: center;
}
</style>
